import {useAtomValue} from 'jotai';
import {useEffect, useMemo} from 'react';
import styled from 'styled-components';
import {ActionGuideTooltip} from '@/components/Tooltip';
import {Typography} from '@/components/Typography';
import {AnalysisItem} from '@/map/@types';
import {useQueryRegionByPoint} from '@/map/api';
import {anaysisListAtom} from '@/map/atom';
import {AreaOverlayType, GeoDataType} from '@/map/enums';
import {panToBoundCircle, panToBoundsPolygon} from '@/map/modules';
import {
  distanceToString,
  flattenPolygonToNaverCoords,
  getGeometryCenterPoint,
  getKm2,
  getPolygonCoords,
  getSidoShortName,
} from '@/map/utils';
import {ColumnFlexBox} from '@/styled-components';
import {palette} from '@/styles';
import {ReactComponent as ArrowCircleIcon} from './assets/arrow-circle-default.svg';
import {ReactComponent as RefreshCircleIcon} from './assets/refresh.svg';

interface IProps {
  map?: naver.maps.Map;
  id: string;
  isError?: boolean;
  afterFetchEmdName?(name: string): void;
  onRefetch?(): void;
}

const getTitle = (item: AnalysisItem) => {
  if (item[AreaOverlayType.POLYGON]?.m2) {
    return `다각형 · 면적 ${getKm2(item[AreaOverlayType.POLYGON].m2)}k㎡`;
  } else if (item[AreaOverlayType.CIRCLE]) {
    return `원형 · 반경 ${distanceToString(item.circle.radius ?? 0, {
      roundingMode: 'ceil',
    })}`;
  } else if (item[AreaOverlayType.EMD]?.name) {
    return '행정동';
  }
};

export const AreaInfo = ({
  map,
  id,
  isError,
  afterFetchEmdName,
  onRefetch,
}: IProps) => {
  const anaysisList = useAtomValue(anaysisListAtom);
  const item = useMemo(
    () => anaysisList.find((i) => i.id === id),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [id],
  );

  const center = useMemo(() => {
    if (item?.type === AreaOverlayType.CIRCLE) {
      return item.circle?.center;
    } else if (item?.type === AreaOverlayType.POLYGON && item.polygon?.paths) {
      const coordinates = getPolygonCoords(item.polygon.paths);

      if (coordinates) {
        return getGeometryCenterPoint([coordinates]);
      }
    }

    return null;
  }, [item]);

  const {data} = useQueryRegionByPoint(
    !!center
      ? {
          lng: center.x,
          lat: center.y,
          type: GeoDataType.Emd,
        }
      : undefined,
  );

  const emdName = useMemo(() => {
    const sido =
      item?.type === AreaOverlayType.EMD ? item.emd?.name : data?.name;

    return sido?.length ? getSidoShortName(sido) : '분석 지역';
  }, [item, data?.name]);

  const onFocusArea = () => {
    if (!map || !item) {
      return;
    }

    const margin = {left: 150, right: 800};

    if (item.polygon?.paths) {
      panToBoundsPolygon(item.polygon.paths, map, margin);
    } else if (item.circle?.center && item.circle.radius) {
      panToBoundCircle(item.circle as any, map, margin);
    } else if (item.emd?.geometry) {
      const flattenedPolygon = flattenPolygonToNaverCoords(
        item.emd.geometry.coordinates,
      ) as any;

      if (flattenedPolygon?.length > 2) {
        panToBoundsPolygon(flattenedPolygon, map, margin);
      }
    }
  };

  useEffect(() => {
    if (emdName) {
      afterFetchEmdName?.(emdName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emdName]);

  if (!item) {
    return null;
  }

  return (
    <Root>
      <TitleSection gap={6}>
        <Title ellipsis>{emdName}</Title>
        <SubText>
          {isError ? '분석 결과 다시 불러오기' : getTitle(item)}
        </SubText>
      </TitleSection>
      {!isError && (
        <ActionGuideTooltip content="위치로 이동" place="top" offset={2}>
          <FocusButton onClick={onFocusArea} />
        </ActionGuideTooltip>
      )}
      {isError && !!onRefetch && (
        <ActionGuideTooltip content="다시 불러오기" place="top" offset={2}>
          <RefreshButton onClick={onRefetch} />
        </ActionGuideTooltip>
      )}
    </Root>
  );
};

const Root = styled.div`
  display: flex;
  width: 100%;
  padding: 16px 12px 15px 12px;
  justify-content: center;
  align-items: center;
  justify-content: space-between;
  border-radius: 8px;
  background: ${palette.gray100};
  line-height: normal;
  gap: 16px;
`;

const TitleSection = styled(ColumnFlexBox)`
  flex-grow: 1;
  min-width: 0;
`;

const Title = styled(Typography)`
  color: ${palette.grayBlack};
  font-size: 14px;
  font-weight: 600;
  display: block;
`;

const SubText = styled.div`
  color: ${palette.gray600};
  font-size: 12px;
  font-weight: 400;
`;

const FocusButton = styled(ArrowCircleIcon)`
  color: ${palette.gray400};
  cursor: pointer;
  outline: none;
  &:hover {
    color: ${palette.gray800};
  }
`;

const RefreshButton = styled(RefreshCircleIcon)`
  color: ${palette.teal100};
  cursor: pointer;
  outline: none;
  &:hover {
    color: ${palette.teal200};
  }
`;
